import { useCallback, useEffect, useRef, useState } from "react";
import { ButtonBase, Icon, Box, styled } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import clsx from "clsx";

// STYLED COMPONENTS
const NavExpandRoot = styled("div")(({ theme }) => ({
  "& .expandIcon": {
    transition: "transform 0.75s cubic-bezier(0, 0, 0.2, 1) 0ms",
    transform: "rotate(90deg)"
  },
  "& .collapseIcon": {
    transition: "transform 0.75s cubic-bezier(0, 0, 0.2, 1) 0ms",
    transform: "rotate(0deg)"
  },
  "& .expansion-panel": {
    overflow: "hidden",
    transition: "max-height 0.75s cubic-bezier(0, 0, 0.2, 1)"
  },
  "& .highlight": {
    background: theme.palette.primary.main
  },
  "&.compactNavItem": {
    width: 44,
    overflow: "hidden",
    justifyContent: "center !important",
    "& .itemText": { display: "none" },
    "& .itemIcon": { display: "none" }
  }
}));

const BaseButton = styled(ButtonBase)(({ theme }) => ({
  //color: theme.palette.text.secondary, 
  height: 35,
  width: "100%",
  whiteSpace: "pre",
  overflow: "hidden",
  paddingRight: "16px",
  borderRadius: "4px",
  marginBottom: "8px",
  display: "flex",
  justifyContent: "space-between !important",
  "&:hover": { background: "rgba(255, 255, 255, 0.08)" },
  "&.parentActive": {
    borderBottom: "0.2px solid rgba(255, 255, 255, 0.5)",
    paddingBottom: "0.2px"
  },
  "& .icon": {
    width: 35,
    fontSize: "18px",
    paddingLeft: "16px",
    paddingRight: "16px",
    verticalAlign: "middle"
  }
}));

const BulletIcon = styled("div")(({ theme }) => ({
  width: 4,
  height: 4,
  color: "inherit",
  overflow: "hidden",
  marginLeft: "20px",
  marginRight: "5px",
  borderRadius: "300px !important",
  background: theme.palette.text.primary
}));

const ItemText = styled("span")(() => ({
  fontSize: "0.875rem",
  paddingLeft: "0.8rem",
  verticalAlign: "middle"
}));

const BadgeValue = styled("div")(() => ({
  padding: "1px 4px",
  overflow: "hidden",
  borderRadius: "300px"
}));

// Helper function to check if a route is active considering child routes
const isRouteActive = (path, location) => {
  // Exact match
  if (location.pathname === path) {
    return true;
  }

  // For routes ending with -table, check if current path matches the base route
  // This handles cases like /recepcion-articulo-table matching /recepcion-articulo/123/update
  if (path.endsWith('-table')) {
    const basePath = path.replace(/-table$/, '');

    // Check if the current path starts with the base path followed by a slash
    // This ensures /recepcion-articulo matches /recepcion-articulo/123/update
    // but not /recepcion-articulo-other
    if (location.pathname.startsWith(basePath + '/')) {
      return true;
    }

    // Also check exact match with base path
    if (location.pathname === basePath) {
      return true;
    }
  }

  return false;
};

export default function MatxVerticalNavExpansionPanel({ item, children, mode }) {
  const [collapsed, setCollapsed] = useState(true);
  const [hasActiveChild, setHasActiveChild] = useState(false);
  const [wasManuallyCollapsed, setWasManuallyCollapsed] = useState(false);
  const elementRef = useRef(null);
  const componentHeight = useRef(0);
  const { pathname } = useLocation();
  const { name, icon, iconText, badge } = item;

  const handleClick = () => {
    componentHeight.current = 0;
    if (elementRef.current) {
      calculateHeight(elementRef.current);
    }
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    // Track if user manually collapsed the panel
    if (newCollapsed && hasActiveChild) {
      setWasManuallyCollapsed(true);
    } else if (!newCollapsed) {
      setWasManuallyCollapsed(false);
    }
  };

  const calculateHeight = useCallback((node) => {
    if (node.name !== "child") {
      for (let child of node.children) {
        calculateHeight(child);
      }
    }

    if (node.name === "child") componentHeight.current += node.scrollHeight;
    else componentHeight.current += 44; //here 44 is node height
    return;
  }, []);

  // Check for active child routes - this runs independently of panel state
  useEffect(() => {
    let foundActiveChild = false;

    // Check if any child route is active by checking the item.children array directly
    // This works even when the panel is collapsed because we check the data, not the DOM
    if (item.children && Array.isArray(item.children)) {
      for (let child of item.children) {
        if (child.path && isRouteActive(child.path, { pathname })) {
          foundActiveChild = true;
          // Only auto-open if it wasn't manually collapsed by the user
          if (!wasManuallyCollapsed) {
            setCollapsed(false);
          }
          break;
        }
      }
    }

    setHasActiveChild(foundActiveChild);
    // Reset manual collapse flag if no active child is found
    if (!foundActiveChild) {
      setWasManuallyCollapsed(false);
    }
  }, [pathname, item.children, wasManuallyCollapsed]);

  // Calculate height when panel is open - this needs the DOM
  useEffect(() => {
    if (!elementRef || !elementRef.current) return;
    if (collapsed) return; // Don't calculate height when collapsed

    calculateHeight(elementRef.current);
  }, [collapsed, calculateHeight]);

  return (
    <NavExpandRoot>
      <BaseButton
        className={clsx({
          "has-submenu compactNavItem": true,
          compactNavItem: mode === "compact",
          open: !collapsed,
          parentActive: hasActiveChild
        })}
        onClick={handleClick}>
        <Box display="flex" alignItems="center">
          {icon && <Icon className="icon">{icon}</Icon>}
          {iconText && <BulletIcon />}
          <ItemText className="sidenavHoverShow">{name}</ItemText>
        </Box>

        {badge && <BadgeValue className="sidenavHoverShow itemIcon">{badge.value}</BadgeValue>}

        <div
          className={clsx({
            sidenavHoverShow: true,
            collapseIcon: collapsed,
            expandIcon: !collapsed
          })}>
          <ChevronRight fontSize="small" sx={{ verticalAlign: "middle" }} />
        </div>
      </BaseButton>

      <div
        ref={elementRef}
        className="expansion-panel submenu"
        style={collapsed ? { maxHeight: "0px" } : { maxHeight: componentHeight.current + "px" }}>
        {children}
      </div>
    </NavExpandRoot>
  );
}
